// Landing page for garden handoff invites.
//
// Flow:
//   1. Homeowner opens the link from the designer's invite email, which
//      contains a token that maps to a garden_handoffs row.
//   2. This page fetches /api/handoff/public/:token to show the designer,
//      project name, zones/plants counts, and any personal note.
//   3. If the homeowner is logged in, they can click "Accept" which POSTs
//      /api/handoff/accept. The server materializes the staging rows into
//      garden_zones / garden_plants / garden_plant_markers / garden_settings
//      under their user id and grants designer co-edit access.
//   4. If they aren't logged in, they can either create an account (email
//      locked to the invited address) or sign in. Both flows return to this
//      page via location.state.returnTo, which auto-accepts once authenticated.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { handoffApi } from './zonesApi';
import { clearDesignerCoEdit } from '../garden/services/gardenApi';

function hasAuthToken() {
  try {
    return Boolean(localStorage.getItem('authToken'));
  } catch {
    return false;
  }
}

function getCurrentUserEmail() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return (u && u.email ? String(u.email) : '').trim().toLowerCase();
  } catch {
    return '';
  }
}

export default function InviteLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const autoAcceptAttempted = useRef(false);

  const load = useCallback(async () => {
    if (!token) {
      setError('This invite link is missing its token.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await handoffApi.publicFetch(token);
      setInvite(resp.handoff || resp.invite || resp);
    } catch (e) {
      setError(e.message || 'We could not load this invite.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const acceptInvite = useCallback(async () => {
    if (!token) return;
    setAccepting(true);
    setAcceptError('');
    try {
      // Any stale designer co-edit context from a previous session would
      // mis-scope /api/garden calls. Clear before accepting so the new
      // homeowner's own garden becomes the active scope.
      clearDesignerCoEdit();
      await handoffApi.accept(token);
      navigate('/garden', { replace: true });
    } catch (e) {
      setAcceptError(e.message || 'Failed to accept invite.');
      setAccepting(false);
    }
  }, [navigate, token]);

  // If the user arrives already authenticated *and* their account email
  // matches the invited address, accept automatically for a zero-click flow.
  useEffect(() => {
    if (autoAcceptAttempted.current) return;
    if (!invite || invite.status !== 'pending') return;
    if (!hasAuthToken()) return;
    const invitedEmail = (invite.homeowner_email || '').trim().toLowerCase();
    const currentEmail = getCurrentUserEmail();
    if (!invitedEmail || !currentEmail || invitedEmail !== currentEmail) return;
    autoAcceptAttempted.current = true;
    acceptInvite();
  }, [invite, acceptInvite]);

  const returnTo = `/garden/invite/${encodeURIComponent(token || '')}`;

  const onCreateAccount = () => {
    navigate('/create-account', {
      state: {
        accountType: 'customer',
        lockedEmail: invite?.homeowner_email || '',
        inviteMessage:
          invite?.designer_name
            ? `You were invited by ${invite.designer_name}. Create your account to load your prepopulated garden.`
            : 'Create your account to load your prepopulated garden.',
        returnTo,
      },
    });
  };

  const onSignIn = () => {
    navigate('/login', {
      state: {
        returnTo,
        prefillEmail: invite?.homeowner_email || '',
        notice: 'Sign in with the email your designer invited to load your garden.',
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 px-4 py-10 flex items-start justify-center">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="text-3xl mb-2">🌿</div>
          <h1 className="text-2xl font-bold text-gray-900">
            Your Garden App is ready
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Your designer has prepared a prepopulated garden care app for you.
          </p>
        </div>

        {loading && (
          <div className="text-sm text-gray-500 text-center py-6">
            Loading your invite…
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
            <div className="font-medium mb-1">We couldn't load this invite.</div>
            <div>{error}</div>
            <div className="mt-3">
              <Link to="/" className="text-red-800 underline">
                Go to the home page
              </Link>
            </div>
          </div>
        )}

        {!loading && !error && invite && (
          <>
            {invite.status === 'accepted' && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-4 text-sm mb-4">
                This invite has already been accepted. Your garden is ready to
                view.
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => navigate('/garden')}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-semibold"
                  >
                    Open my Garden App
                  </button>
                </div>
              </div>
            )}

            {invite.status === 'cancelled' && (
              <div className="bg-gray-50 border border-gray-200 text-gray-800 rounded-lg p-4 text-sm mb-4">
                This invite was cancelled by your designer. Please contact them
                if you still expect to receive access.
              </div>
            )}

            {invite.status === 'expired' && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4 text-sm mb-4">
                This invite has expired. Please ask your designer to resend it.
              </div>
            )}

            {invite.status === 'pending' && (
              <>
                <div className="border border-gray-200 rounded-lg p-4 mb-4 text-sm space-y-1">
                  {invite.designer_name && (
                    <div>
                      <span className="text-gray-500">Designer:</span>{' '}
                      <span className="font-medium text-gray-900">{invite.designer_name}</span>
                    </div>
                  )}
                  {invite.project_name && (
                    <div>
                      <span className="text-gray-500">Project:</span>{' '}
                      <span className="font-medium text-gray-900">{invite.project_name}</span>
                    </div>
                  )}
                  {invite.homeowner_email && (
                    <div>
                      <span className="text-gray-500">Invited email:</span>{' '}
                      <span className="font-medium text-gray-900">{invite.homeowner_email}</span>
                    </div>
                  )}
                  <div className="text-gray-600 pt-1">
                    {Number(invite.zone_count) || 0} zones ·{' '}
                    {Number(invite.plant_count) || 0} plants ready to load
                  </div>
                </div>

                {invite.message && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-3 text-sm mb-4">
                    <div className="font-medium mb-1">Note from your designer</div>
                    <div className="whitespace-pre-wrap">{invite.message}</div>
                  </div>
                )}

                {acceptError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm mb-4">
                    {acceptError}
                  </div>
                )}

                {hasAuthToken() ? (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={acceptInvite}
                      disabled={accepting}
                      className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-lg hover:from-emerald-700 hover:to-teal-700 font-semibold shadow-md disabled:opacity-60"
                    >
                      {accepting ? 'Loading your garden…' : 'Accept and open my Garden App'}
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      You're signed in as{' '}
                      <strong>{getCurrentUserEmail() || 'your account'}</strong>.
                      If this isn't the email your designer invited, sign out
                      and use the correct account.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={onCreateAccount}
                      className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-lg hover:from-emerald-700 hover:to-teal-700 font-semibold shadow-md"
                    >
                      Create my account
                    </button>
                    <button
                      type="button"
                      onClick={onSignIn}
                      className="w-full border border-gray-300 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-50 font-semibold"
                    >
                      I already have an account
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      We'll load your prepopulated garden automatically once
                      you're signed in.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
